import {
  Box,
  Tooltip as MuiTooltip,
  tooltipClasses,
  type TooltipProps,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

import {
  getNewsSentimentColor,
  PLOT_PADDING_Y,
  Y_AXIS_WIDTH,
} from "~/feature/chart/constants";
import type { INewsSentiment } from "~/feature/chart/types";

import {
  DOT_HALO,
  getTooltipSurface,
  LINE_OPACITY,
  TOOLTIP_MAX_WIDTH,
} from "./constants";

export const Root = styled(Box)({
  position: "absolute",
  top: PLOT_PADDING_Y,
  bottom: 0,
  left: Y_AXIS_WIDTH,
  right: 0,
  zIndex: 1,
  pointerEvents: "none",
});

export const Line = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$leftPercent" && prop !== "$sentiment",
})<{ $leftPercent: number; $sentiment: INewsSentiment | null }>(
  ({ $leftPercent, $sentiment, theme }) => ({
    position: "absolute",
    top: 0,
    bottom: 0,
    left: `${$leftPercent}%`,
    borderLeft: `1px dashed ${alpha(
      getNewsSentimentColor(theme, $sentiment),
      LINE_OPACITY
    )}`,
  })
);

export const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$leftPercent",
})<{ $leftPercent: number }>(({ $leftPercent }) => ({
  position: "absolute",
  bottom: PLOT_PADDING_Y,
  left: `${$leftPercent}%`,
  transform: "translateX(-50%)",
  display: "flex",
}));

export const Badge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$sentiment" && prop !== "$dotSize",
})<{ $sentiment: INewsSentiment | null; $dotSize: number }>(({
  $sentiment,
  $dotSize,
  theme,
}) => {
  const color = getNewsSentimentColor(theme, $sentiment);

  return {
    position: "relative",
    pointerEvents: "auto",
    width: $dotSize,
    height: $dotSize,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: theme.palette.common.white,
    backgroundColor: color,
    boxShadow: `0 0 0 ${DOT_HALO}px ${alpha(color, 0.18)}`,

    "& .MuiSvgIcon-root": {
      fontSize: $dotSize - 6,
    },
  };
});

export const Count = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -5,
  right: -7,
  minWidth: 13,
  height: 13,
  padding: theme.spacing(0, 0.375),
  borderRadius: 7,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1,
  color: theme.palette.background.paper,
  backgroundColor: theme.palette.text.primary,
  boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
}));

export const Tooltip = styled(({ className, ...props }: TooltipProps) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: TOOLTIP_MAX_WIDTH,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: getTooltipSurface(theme).surface,
    boxShadow: theme.shadows[6],
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: getTooltipSurface(theme).surface,
  },
}));
